import { ZEITFENSTER, ZEITFENSTER_LABELS, type Zeitfenster } from '@netcon-work/shared'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Trichter } from '../components/Trichter'
import { Zeitachse } from '../components/Zeitachse'
import { Topbar } from '../components/Topbar'
import { Card, CardPad, SectionTitle } from '../components/ui/primitives'
import { meilensteineQuery, planungQuery, vorhabenQuery } from '../lib/queries'
import { trpc } from '../lib/trpc'

/**
 * **Planung: Zeitfenster statt Sprints** (Phase 8, Mockup `v-planung`, Konzept Kap. 4.5).
 *
 * Geplant wird das **Nachfüllen der Pools**, nicht der Arbeitstag einer Person. Deshalb steht
 * hier keine Personenzeile und kein Balken je Mensch – sobald diese Ansicht anfängt, jemandem
 * Tage zuzuweisen, ist sie falsch geworden.
 *
 * ## Warum kein Burndown und keine Velocity
 *
 * Die Versuchung ist groß, weil es bekannte Muster sind. Das Konzept will es ausdrücklich
 * anders (Kap. 4.5): **Verschieben ist normal, es gibt kein „Sprint gescheitert".** Was hier
 * steht, ist eine Auskunft – *reicht die Zeit?* –, keine Bewertung.
 */
export function Planung() {
  const { data, isLoading } = useQuery(planungQuery)

  return (
    <>
      <Topbar
        titel="Planung"
        untertitel="Zeitfenster statt Sprints – geplant wird das Nachfüllen der Pools"
      />

      {isLoading && (
        <Card>
          <CardPad className="text-[13px] text-muted">Lädt …</CardPad>
        </Card>
      )}

      {/**
       * **Der Trichter steht oben** (Nils, 17.08.2026). Er ersetzt die Pool-Ansicht
       * ausdrücklich nicht – er ist die grafische Fassung davon, ergänzt um den Weg
       * danach. In der Planung, weil hier die Frage gestellt wird: *staut es sich, und wo?*
       */}
      <SectionTitle hint="Zulauf, Pools und der Weg danach – die Form ist die Stellung, die Füllung die Menge">
        Trichter
      </SectionTitle>
      <Trichter />

      {/**
       * **Die Zeitachse steht über allem anderen** (19.08.2026). Nils: *„ich brauche das in der
       * Planung alles."* Sie beantwortet die Frage, mit der man diese Ansicht öffnet – *wer hat
       * was wann vor sich* –, und braucht dafür weder `planungQuery` noch einen Ladezustand:
       * sie rechnet aus dem Bestand, der ohnehin im Client liegt (Leitplanke 2).
       */}
      <SectionTitle hint="wer hat was wann vor sich – abgeleitet aus Fälligkeit, Meilenstein, Zieltermin, Zeitfenster">
        Zeitachse
      </SectionTitle>
      <Zeitachse />

      {data && (
        <>
          <Zeitfensterleiste />

          <SectionTitle hint="Aus dem Ereignis-Log der letzten Wochen – gemessen, nicht geschätzt">
            Durchsatz
          </SectionTitle>
          <Card className="mb-4">
            <CardPad>
              <div className="flex flex-wrap items-baseline gap-4 text-[13px]">
                <span>
                  <strong className="text-[18px]">{data.durchsatz.median}</strong> Aufgaben je Woche{' '}
                  <span className="text-muted">(Median)</span>
                </span>
                <span className="text-ink2">
                  schwache Woche {data.durchsatz.schwach} · starke Woche {data.durchsatz.stark}
                </span>
                <span className="ml-auto text-[11.5px] text-muted">
                  {data.durchsatz.wochen} Wochen gemessen
                </span>
              </div>
              {/**
               * **Der Satz, der die Zahl richtig lesbar macht.** Ohne ihn liest jemand den
               * Median als Zusage. Die Spanne ist Faktor drei – das ist keine Ungenauigkeit
               * der Messung, sondern die Wirklichkeit.
               */}
              <p className="mt-2 text-[11.5px] text-muted">
                Gerechnet wird mit der <strong>schwachen</strong> Woche, nicht mit dem Median:
                „reicht auch im schlechten Fall" ist eine Aussage, „reicht" wäre eine Behauptung.
                Schätzungen gibt es im Bestand keine – <strong>0 von 150</strong> offenen Aufgaben
                tragen eine –, deshalb steht diese Rechnung auf dem gemessenen Durchsatz.
              </p>
            </CardPad>
          </Card>

          <SectionTitle hint="Wie viel Lieferkraft in welcher Woche da ist – nach Beitrag gewichtet">
            Kalenderwochen
          </SectionTitle>
          <Card className="mb-4">
            <CardPad>
              {data.lage.slice(0, 13).map((w) => (
                <div key={w.kw} className="flex items-center gap-2.5 py-[3px] text-[12px]">
                  <span className="tabular w-[62px] shrink-0 text-muted">{w.kw}</span>
                  <div className="h-[9px] w-[160px] shrink-0 overflow-hidden rounded-full bg-[#f0efec]">
                    <i
                      className={`block h-full rounded-full ${
                        w.verfuegbar < 0.75
                          ? 'bg-pool-sofort'
                          : w.verfuegbar < 0.9
                            ? 'bg-[#c89a3c]'
                            : 'bg-accent'
                      }`}
                      style={{ width: `${w.verfuegbar * 100}%` }}
                    />
                  </div>
                  <span className="tabular w-[42px] shrink-0 text-ink2">
                    {Math.round(w.verfuegbar * 100)} %
                  </span>
                  <span className="min-w-0 truncate text-[11.5px] text-muted">
                    {w.abwesend.length === 0
                      ? 'niemand aus dem Team weg'
                      : w.abwesend.map((a) => `${a.name} (${a.tage} T)`).join(', ')}
                  </span>
                </div>
              ))}
              {/**
               * **Warum jemand in der Liste stehen kann, ohne den Balken zu bewegen.**
               *
               * Die Lieferkraft ist nach Beitrag gewichtet: wer im gemessenen Zeitraum kaum
               * Aufgaben abgeschlossen hat, senkt sie kaum. Das ist keine Wertung – es ist die
               * Antwort auf die Frage, die hier gestellt wird: *wie viel schaffen wir in dieser
               * Woche?*
               */}
              <p className="mt-2 text-[11.5px] text-muted">
                Der Balken ist die <strong>Lieferkraft</strong>, nicht die Anwesenheit: gewichtet
                mit dem Anteil, den jemand am gemessenen Durchsatz hatte. Deshalb kann jemand in der
                Liste stehen, ohne den Balken zu bewegen.
              </p>
            </CardPad>
          </Card>

          <SectionTitle hint="Reicht die Zeit bis zum Zieltermin – mit abgezogenen Abwesenheiten">
            Vorhaben mit Zieltermin
          </SectionTitle>
          {data.vorhaben.length === 0 ? (
            <Card>
              <CardPad className="text-[12.5px] text-ink2">
                Kein Vorhaben mit Zieltermin. Sobald eines einen hat, steht hier, ob die Zeit reicht
                – gerechnet aus offenen Aufgaben, gemessenem Durchsatz und der Lieferkraft der
                Wochen bis dahin.
              </CardPad>
            </Card>
          ) : (
            <div className="grid gap-2">
              {data.vorhaben.map((v) => (
                <Card key={v.vorhabenId}>
                  <CardPad className="flex flex-wrap items-baseline gap-3 text-[12.5px]">
                    <strong>{v.label}</strong>
                    <span
                      className={`rounded-md px-2 py-px text-[11px] font-semibold ${
                        v.urteil === 'passt'
                          ? 'bg-[#e5f7f0] text-[#0d7a52]'
                          : v.urteil === 'knapp'
                            ? 'bg-[#fdf3dc] text-[#8a6100]'
                            : 'bg-[#fdeaea] text-pool-sofort'
                      }`}
                    >
                      {v.urteil === 'passt' ? 'passt' : v.urteil === 'knapp' ? 'knapp' : 'reißt'}
                    </span>
                    <span className="text-ink2">
                      {v.offen} offen · Ziel{' '}
                      {new Date(v.zieltermin).toLocaleDateString('de-DE', {
                        day: '2-digit',
                        month: '2-digit',
                      })}
                    </span>
                    <span className="ml-auto text-[11.5px] text-muted">
                      braucht {v.brauchtStark}–{v.brauchtSchwach} Wochen · verfügbar {v.teamwochen}{' '}
                      von {v.wochenBis}
                    </span>
                  </CardPad>
                </Card>
              ))}
            </div>
          )}

          <p className="mt-4 text-[11.5px] text-muted">
            Die Rechnung nimmt an, dass das Team <em>nur</em> an diesem Vorhaben arbeitet – ein
            Anteil je Vorhaben wäre eine Annahme, die niemand getroffen hat. Und:{' '}
            <strong>Verschieben ist normal.</strong> „Reißt" heißt „so wird es nicht", nicht „hier
            ist etwas schiefgelaufen".
          </p>
        </>
      )}
    </>
  )
}

/**
 * **Die Zeitfenster-Leiste** (Phase 8, Mockup `v-planung`, Konzept Kap. 4.5).
 *
 * Von Nils am 04.08.2026 geschnitten: *„jetzt, innerhalb 5 Tage, innerhalb 10 Tage, Monat,
 * Quartal."*
 *
 * ## Warum Dringlichkeitsstufen und keine Sprints
 *
 * Ein Sprint ist ein Zeitraum mit Anfang, Ende und einer Zusage. Ein Zeitfenster ist eine
 * **Stufe**: „innerhalb 5 Tage" bleibt „innerhalb 5 Tage", auch drei Tage später gelesen.
 * Deshalb gibt es hier kein „verschoben" und kein „gescheitert" – verschieben heißt, ein
 * Vorhaben in ein anderes Fenster zu ziehen, und das ist ein normaler Vorgang.
 *
 * ## Warum „nicht eingeordnet" mitläuft
 *
 * Ein Vorhaben ohne Fenster ist der Normalfall direkt nach dem Anlegen. Es wegzulassen hieße,
 * genau die zu verstecken, um die man sich kümmern muss.
 */
function Zeitfensterleiste() {
  const queryClient = useQueryClient()
  const { data: vorhaben = [] } = useQuery(vorhabenQuery)
  const { data: meilensteine = [] } = useQuery(meilensteineQuery)

  /**
   * **Verschieben ist ein normaler Vorgang** – das ist ein Abnahmekriterium der Phase 8 und
   * war bis zum 17.08.2026 nicht gebaut. Nils: *„Zeitfenster lassen sich noch gar nicht
   * steuern oder bearbeiten."*
   *
   * Ein Auswahlfeld je Zeile, kein Ziehen mit der Maus. Fünf Stufen und eine Handvoll
   * Einträge – Drag & Drop wäre hier mehr Bedienung für dieselbe Entscheidung, und mit der
   * Tastatur unerreichbar (Leitplanke 3).
   */
  const verschieben = useMutation({
    mutationFn: (v: { id: string; zeitfenster: Zeitfenster | null }) =>
      trpc.vorhaben.aendern.mutate({ id: v.id, zeitfenster: v.zeitfenster }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vorhaben'] }),
  })

  const aktive = vorhaben.filter((v) => v.aktiv)
  if (aktive.length === 0) return null

  const fenster: { wert: string | null; label: string }[] = [
    ...ZEITFENSTER.map((z: Zeitfenster) => ({
      wert: z as string | null,
      label: ZEITFENSTER_LABELS[z],
    })),
    { wert: null, label: 'Noch nicht eingeordnet' },
  ]

  return (
    <>
      <SectionTitle hint="Dringlichkeitsstufen statt Sprints – verschieben ist normal">
        Vorhaben nach Zeitfenster
      </SectionTitle>
      <div className="mb-4 grid gap-2">
        {fenster.map((f) => {
          const drin = aktive.filter((v) => (v.zeitfenster ?? null) === f.wert)
          if (drin.length === 0) return null
          return (
            <Card key={f.label}>
              <CardPad className="flex flex-wrap items-baseline gap-2 text-[12.5px]">
                <strong className="w-[150px] shrink-0">{f.label}</strong>
                {drin.map((v) => {
                  /**
                   * Nur **terminierte** Meilensteine: die Frage dieser Ansicht ist „wann muss
                   * das fertig sein?", und einer ohne Datum beantwortet sie nicht (E-273).
                   */
                  const naechster = meilensteine
                    .filter(
                      (m): m is typeof m & { termin: string } =>
                        m.vorhabenId === v.id && !m.erreichtAm && m.termin !== null,
                    )
                    .sort((a, b) => a.termin.localeCompare(b.termin))[0]
                  return (
                    <span
                      key={v.id}
                      className="rounded-md bg-[#f3f2ef] px-2 py-0.5"
                      title={
                        naechster
                          ? `Nächster Meilenstein: ${naechster.label} am ${naechster.termin}`
                          : 'Kein offener Meilenstein'
                      }
                    >
                      {v.label}
                      <span className="ml-1.5 text-muted">{v.stand.offen} offen</span>
                      {/**
                       * **Der nächste Meilenstein steht am Vorhaben**, nicht in einer eigenen
                       * Zeile: er beantwortet dieselbe Frage wie das Fenster – *wann muss das
                       * fertig sein?* – nur genauer.
                       */}
                      {naechster && (
                        <span className="ml-1.5 text-[11px] text-accent-deep">
                          ◆{' '}
                          {new Date(naechster.termin).toLocaleDateString('de-DE', {
                            day: '2-digit',
                            month: '2-digit',
                          })}
                        </span>
                      )}
                      {/**
                       * **Verschieben steht an der Marke, nicht in einem Menü darüber.**
                       * Wer ein Vorhaben in ein anderes Fenster ziehen will, sieht es
                       * gerade an – dorthin gehört die Handlung.
                       */}
                      <select
                        value={v.zeitfenster ?? ''}
                        onChange={(e) =>
                          verschieben.mutate({
                            id: v.id,
                            zeitfenster: (e.target.value || null) as Zeitfenster | null,
                          })
                        }
                        aria-label={`Zeitfenster von ${v.label}`}
                        className="ml-1.5 cursor-pointer rounded border border-line2 bg-surface px-1 text-[10.5px]"
                      >
                        <option value="">–</option>
                        {ZEITFENSTER.map((z: Zeitfenster) => (
                          <option key={z} value={z}>
                            {ZEITFENSTER_LABELS[z]}
                          </option>
                        ))}
                      </select>
                    </span>
                  )
                })}
              </CardPad>
            </Card>
          )
        })}
      </div>
    </>
  )
}
